import React, { useState, useEffect } from "react";
import * as yup from "yup";
import { message } from "antd";
import OForm from "../common/OForm";
import {
  getStaffById,
  getFacilitiesByStaffId,
  updateStaffById,
} from "../../utils/apiClient";
import CustomContainer from "../common/CustomContainer";
import ThemedChip from "../common/ThemedChip";
import { isValidUSPhoneNumber, formatPhoneNumber, formatPhoneNumberForEdit } from "../../utils/helpers/format-phone-number";
import { useDetectMyRole } from "../../custom-hooks";
import { Button } from "@voicecare.ai/ui-library";
import ChangePasswordDialog from "./ChangePasswordDialog";

const UserProfile = () => {
  const [initialValues, setInitialValues] = useState({
    fullName: "",
    email: "",
    phoneNumber: "",
    facilities: [],
    roles: [],
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [changePasswordDialogOpen, setChangePasswordDialogOpen] = useState(false);

  const { role } = useDetectMyRole();


  // Validation schema
  const validationSchema = yup.object().shape({
    fullName: yup.string().required("Full name is required"),
    email: yup.string().email("Invalid email").required("Email is required"),
    phoneNumber: yup
      .string()
      .required("Phone number is required")
      .test('valid-phone', 'Enter a valid US phone number', (value) => {
        return isValidUSPhoneNumber(value);
      }),
  });

  // Get staff ID from session storage
  const getStaffId = () => {
    try {
      const staffInfo = sessionStorage.getItem("staff_info");
      if (staffInfo) {
        const parsedStaffInfo = JSON.parse(staffInfo);
        return parsedStaffInfo._id;
      }
      return null;
    } catch (error) {
      console.error("Error parsing staff info:", error);
      return null;
    }
  };

  // API call to get staff data
  const getStaffData = async (staffId) => {
    try {
      const response = await getStaffById(staffId);
      return response;
    } catch (error) {
      console.error("Error fetching staff data:", error);
      throw error;
    }
  };

  // API call to get facilities
  const getFacilitiesData = async (staffId) => {
    try {
      const response = await getFacilitiesByStaffId(staffId);
      return response;
    } catch (error) {
      console.error("Error fetching facilities data:", error);
      throw error;
    }
  };

  // API call to update staff data
  const updateStaffData = async (staffId, updateData) => {
    try {
      const response = await updateStaffById(staffId, updateData);
      return response;
    } catch (error) {
      console.error("Error updating staff data:", error);
      throw error;
    }
  };

  // Load user data on component mount
  useEffect(() => {
    const loadUserData = async () => {
      try {
        const staffId = getStaffId();
        if (!staffId) {
          console.error("Staff ID not found in session storage");
          return;
        }

        // Fetch staff data
        const staffData = await getStaffData(staffId);
        if (staffData.statusCode === "SUCCESS") {
          const staff = staffData.response;

          let facilitiesData = [];
          // Fetch facilities data if staff has facility IDs
          if (staff.facilityIds && staff.facilityIds.length > 0) {
            try {
              const facilitiesResponse = await getFacilitiesData(staffId);
              if (facilitiesResponse.statusCode === "SUCCESS") {
                facilitiesData = facilitiesResponse.response || [];
              }
            } catch (facilitiesError) {
              console.error("Error fetching facilities:", facilitiesError);
              // Continue without facilities data
            }
          }

          setInitialValues({
            fullName: staff.name || "",
            email: staff.email || "",
            phoneNumber: formatPhoneNumberForEdit(staff.phoneNumber) || "",
            facilities: facilitiesData
              .map((facility) => facility.name || facility.facilityName || "")
              .filter((name) => name),
            roles: staff.role || [],
          });

        }
      } catch (error) {
        console.error("Error loading user data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadUserData();
  }, [role]);


  // Form fields definition
  const getFields = (watch, { setValue, getValues }) => [
    {
      fieldName: "fullName",
      renderType: "text",
      label: "Full Name",
      isRequired: true,
      disabled: false,
      section: "Personal Information",
    },
    {
      fieldName: "email",
      renderType: "text",
      label: "Email ID",
      isRequired: true,
      disabled: true,
      section: "Personal Information",
    },
    {
      fieldName: "phoneNumber",
      renderType: "phoneNumber",
      label: "Phone Number",
      isRequired: true,
      disabled: false,
      section: "Personal Information",
    },
    {
      fieldName: "facilities",
      renderType: "custom",
      label: "Facilities",
      section: "Facilities",
      sectionInfo: "All the facilities assigned to you",
      render: () => (
        <div style={{ padding: "0.75rem 0" }}>
          {initialValues.facilities.length > 0 ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
              {initialValues.facilities.map((facility, index) => (
                <ThemedChip key={index} >
                  {facility}
                </ThemedChip>
              ))}
            </div>
          ) : (
            <span style={{ color: "#666", fontStyle: "italic" }}>
              {role === 'admin' ? "You have access to all facilities" : "No facilities assigned to you"}
            </span>
          )}
        </div>
      ),
    },
    {
      fieldName: "roles",
      renderType: "custom",
      label: "Roles",
      section: "Roles",
      render: () => (
        <div style={{ padding: "0.75rem 0" }}>
          {initialValues.roles.length > 0 ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
              {initialValues.roles.map((role, index) => (
                <ThemedChip key={index}>
                  {role}
                </ThemedChip>
              ))}
            </div>
          ) : (
            <span style={{ color: "#666", fontStyle: "italic" }}>
              No roles assigned
            </span>
          )}
        </div>
      ),
    },
  ];

  // Form submit handler
  const handleSubmit = async (formData) => {
    setSubmitting(true);
    try {
      const staffId = getStaffId();
      if (!staffId) {
        message.error("Staff ID not found. Please login again.");
        return;
      }

      // Send the fields that can be updated (name and phoneNumber)
      const updateData = {
        name: formData.fullName,
        phoneNumber: formatPhoneNumber(formData.phoneNumber),
      };

      const response = await updateStaffData(staffId, updateData);
      if (response.statusCode === "SUCCESS") {
        message.success("Profile updated successfully!");
        
        // Update session storage with new data
        const staffInfo = JSON.parse(sessionStorage.getItem("staff_info"));
        staffInfo.name = formData.fullName;
        staffInfo.phoneNumber = formatPhoneNumber(formData.phoneNumber);
        sessionStorage.setItem("staff_info", JSON.stringify(staffInfo));

        // Dispatch custom event to notify Layout component about the update
        window.dispatchEvent(new CustomEvent('userProfileUpdated'));

        // Refresh data by calling getStaffById
        await refreshUserData(staffId);
      } else {
        message.error("Failed to update profile. Please try again.");
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      message.error("An error occurred while updating profile. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Function to refresh user data
  const refreshUserData = async (staffId) => {
    try {
      // Fetch updated staff data
      const staffData = await getStaffData(staffId);
      if (staffData.statusCode === "SUCCESS") {
        const staff = staffData.response;
        
        let facilitiesData = [];
        // Fetch facilities data if staff has facility IDs
        if (staff.facilityIds && staff.facilityIds.length > 0) {
          try {
            const facilitiesResponse = await getFacilitiesData(staffId);
            if (facilitiesResponse.statusCode === "SUCCESS") {
              facilitiesData = facilitiesResponse.response || [];
            }
          } catch (facilitiesError) {
            console.error("Error fetching facilities:", facilitiesError);
            // Continue without facilities data
          }
        }

        // Update the form values with fresh data
        setInitialValues({
          fullName: staff.name || "",
          email: staff.email || "",
          phoneNumber: formatPhoneNumberForEdit(staff.phoneNumber) || "",
          facilities: facilitiesData
            .map((facility) => facility.name || facility.facilityName || "")
            .filter((name) => name),
          roles: staff.role || [],
        });
      }
    } catch (error) {
      console.error("Error refreshing user data:", error);
      message.warning("Profile updated but failed to refresh display data.");
    }
  };

  const handleChangePassword = () => {
    setChangePasswordDialogOpen(true);
  };

  const changePasswordButton = (
    <Button
      variant="contained"
      onClick={handleChangePassword}
      label="Change Password"
      style={{ borderRadius: "6px" }}
    />
  );

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "200px",
        }}
      >
        Loading...
      </div>
    );
  }

  return (
    <>
      <CustomContainer>
        <OForm
          // formTitle="User Profile"
          initialValues={initialValues}
          fields={getFields}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          fieldsPerRow={3}
          submitButtonText="Update Profile"
          submitting={submitting}
          additionalButtons={[changePasswordButton]}
        />
      </CustomContainer>

      <ChangePasswordDialog
        open={changePasswordDialogOpen}
        onClose={() => setChangePasswordDialogOpen(false)}
      />
    </>
  );
};

export default UserProfile;
